import React, {useEffect, useRef, useState} from "react";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {DatePicker} from "../../common/DatePicker";
import ColorBox from "../../common/ColorBox";
import Editor from "../../common/Editor";
import {MODE_CREATE, MODE_EDIT} from "../../codes";
import moment from "moment";
import {apiCreateSite, apiUpdateSite} from "../../api/ApiSites";
import {useMutation} from "@tanstack/react-query";
import useToast from "../../util/useToast";
import {useNavigate} from "react-router";
import {SiteErrors} from "../../errors/ErrorsMapping";
import useAppContext from "../../useAppContext";
import {AppStaticDropdown} from "../../common/AppStaticDropdown";
import { TextEditor } from "../../lexical/TextEditor";

const SiteEditor = ({show, mode, hide, onSubmit, site = {}}) => {
  const {showToastSuccess} = useToast();
  const {sitesStatuses, getDefaultSiteStatus} = useAppContext();

  const defaultStatusId = getDefaultSiteStatus().id;

  const navigate = useNavigate();

  const nameRef = useRef(null);
  const editorRef = useRef(null);

  const [formErrors, setFormErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    description: "",
    dateStart: moment().format("YYYY-MM-DD"),
    dateEnd: null,
    color: "#555555|#FFFFFF"
  });

  const createMutation = useMutation(apiCreateSite());
  const editMutation = useMutation(apiUpdateSite(site.id));

  useEffect(() => {
    if (show) {
      setFormErrors({});

      setFormData({
        name: site.name || "",
        address: site.address || "",
        description: site.description || "",
        dateStart: site.dateStart || moment().format("YYYY-MM-DD"),
        dateEnd: site.dateEnd || null,
        color: site.color || "#555555|#FFFFFF",
        statusId: site.status || defaultStatusId,
      });

      nameRef.current.focus();
    }
  }, [show, site, defaultStatusId]);

  const submit = () => {
    let promise;

    const submitData = {
      ...formData,
      description: editorRef.current?.getHtml() || ""
    };

    if (mode === MODE_EDIT) {
      promise = editMutation.mutateAsync(submitData);
    } else {
      promise = createMutation.mutateAsync(submitData);
    }

    return promise
      .then(response => {
        return (onSubmit ? onSubmit() : Promise.resolve()).then(() => {
          if (mode === MODE_EDIT) {
            showToastSuccess("Chantier modifié");
          } else {
            showToastSuccess("Chantier ajouté");

            navigate("/sites/" + response.id);
          }
        });
      })
      .then(hide)
      .catch((err) => {
        if (err.response?.status === 400) {
          setFormErrors(err.response.data.errors);
        }
      });
  };

  const updateFormData = field => e => {
    const values = {};

    switch (field) {
      case "dateStart":
      case "dateEnd":
        values[field] = e;

        if (e && formData.dateEnd && moment(e).isAfter(moment(formData.dateEnd))) {
          values["dateEnd"] = e;
        }

        break;
      case "statusId":
      case "color":
        values[field] = e;
        break;
      default:
        values[field] = e.target.value;
        break;
    }

    setFormData(prev => ({...prev, ...values}));
  };

  return <Editor
    show={show}
    title={mode === MODE_CREATE ? "Ajouter un chantier" : "Modifier un chantier"}
    onSubmit={submit}
    onCancel={hide}
    submitLabel="Sauvegarder"
    cancelLabel="Annuler"
  >
    <Form.Group className="mb-2" controlId="site-editor-name">
      <Row>
        <Form.Label column sm={3}>Nom</Form.Label>
        <Col sm={9}>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={updateFormData("name")}
            maxLength="256"
            ref={nameRef}
            isInvalid={!!formErrors.name}
          />

          <Form.Control.Feedback type="invalid">{SiteErrors.name[formErrors.name]}</Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-editor-address">
      <Row>
        <Form.Label column sm={3}>Adresse</Form.Label>
        <Col sm={9}>
          <Form.Control
            type="text"
            value={formData.address}
            onChange={updateFormData("address")}
            maxLength="512"
            isInvalid={!!formErrors.address}
          />
          <Form.Control.Feedback type="invalid">{SiteErrors.address[formErrors.address]}</Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-editor-description">
      <Row>
        <Form.Label column sm={3}>Description</Form.Label>
        <Col sm={9}>
          <TextEditor ref={editorRef} value={formData.description} />
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-editor-status">
      <Row>
        <Form.Label column sm={3}>Statut</Form.Label>
        <Col sm={9}>
          <AppStaticDropdown
            value={formData.statusId}
            choices={sitesStatuses.map(status => ({value: status.id, label: status.name}))}
            onChange={updateFormData("statusId")}
          />
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-editor-date-start">
      <Row>
        <Form.Label column sm={3}>Date de début</Form.Label>
        <Col sm={9}>
          <DatePicker
            value={formData.dateStart}
            onChange={updateFormData("dateStart")}
            isInvalid={!!formErrors.dateStart}
            error={SiteErrors.dateStart[formErrors.dateStart]}
          />
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-editor-date-end">
      <Row>
        <Form.Label column sm={3}>Date de fin</Form.Label>
        <Col sm={9}>
          <DatePicker value={formData.dateEnd} onChange={updateFormData("dateEnd")}/>
        </Col>
      </Row>
    </Form.Group>

    <Form.Group className="mb-2" controlId="site-editor-color">
      <Row>
        <Form.Label column sm={3}>Couleur</Form.Label>
        <Col sm={9}>
          <ColorBox value={formData.color} onChange={updateFormData("color")}/>
        </Col>
      </Row>
    </Form.Group>
  </Editor>;
};

export default SiteEditor;